<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Logo from '@/assets/Apexx_LOGO.webp';
import {
	loginWithKeycloak,
	onNativeLoginFailed,
	shouldUseNativeOtpApiLogin,
} from '@/auth/setup';
import { resumePendingPushIfAny } from '@/push/setupPushNavigation';
import { resumePendingShareIfAny } from '@/share/setupShareTarget';
import { useHubAuthStore } from '@/store/hubAuthStore';
import { clearNativeLoginFailure, isNativeLoginInProgress } from '@apexx/auth';
import { Button, Icon, Typography, Wizard } from '@apexx/component-library';
import { motion } from 'motion-v';

type UserType = 'client' | 'staff';

;

const authStore = useHubAuthStore();
const router = useRouter();
const route = useRoute();
const sessionError = ref<string>();
const loginCancelled = ref(false);
const browserClosedWithoutAuth = ref(false);

const userType = computed<UserType>(() =>
	route.query.type === 'staff' ? 'staff' : 'client',
);

const loginTypeLabel = computed(() =>
	userType.value === 'client' ? 'Customer Login' : 'Employee Login',
);

async function resumePendingNavigationAfterAuth(): Promise<boolean> {
	if (await resumePendingPushIfAny(router, authStore.accessToken)) {
		return true;
	}

	return resumePendingShareIfAny(router, authStore.accessToken);
}

async function handleBack(): Promise<void> {
	loginCancelled.value = true;
	await router.push({
		name: route.query.from === 'reauth' ? 'reauth' : 'login',
	});
}

async function handleRedirect(): Promise<void> {
	if (loginCancelled.value) {
		return;
	}

	if (authStore.isLoggedIn) {
		if (await resumePendingNavigationAfterAuth()) {
			return;
		}

		await router.replace({ name: 'launcher' });
		return;
	}

	if (isNativeLoginInProgress()) {
		return;
	}

	browserClosedWithoutAuth.value = false;
	clearNativeLoginFailure();

	try {
		await loginWithKeycloak();
	} catch {
		sessionError.value =
			'Unable to start sign in. Check login configuration.';
	}
}

onNativeLoginFailed(() => {
	browserClosedWithoutAuth.value = true;
});

watch(
	() => authStore.isLoggedIn,
	async (loggedIn) => {
		if (loggedIn && !loginCancelled.value) {
			if (await resumePendingNavigationAfterAuth()) {
				return;
			}

			await router.replace({ name: 'launcher' });
		}
	},
);

onMounted(() => {
	if (shouldUseNativeOtpApiLogin()) {
		void router.replace({
			name: 'login-otp',
			query: { type: userType.value },
		});
		return;
	}

	handleRedirect();
});

onUnmounted(() => {
	onNativeLoginFailed(() => {});
});
</script>

<template>
	<Wizard
		content-padding="lg"
		centered
		root-class="min-h-dvh"
		content-class="relative pb-[calc(2.5rem+var(--safe-area-bottom))]"
	>
		<template #header>
			<div class="flex flex-col items-center justify-between gap-10 py-20">
				<div class="relative">
					<button
						type="button"
						class="absolute top-4 -left-10 z-10 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white"
						aria-label="Go back to login options"
						@click="handleBack"
					>
						<Icon name="arrowLeft" />
					</button>
					<img
						:src="Logo"
						alt="Apexx logo"
						class="h-16 overflow-hidden rounded"
					/>
				</div>
				<div>
					<span class="flex items-center gap-2 text-white">
						<Icon
							:name="userType === 'client' ? 'person' : 'machines'"
							class="scale-150"
						/>
						{{ loginTypeLabel }}
					</span>
				</div>
			</div>
		</template>

		<motion.div
			:initial="{ opacity: 0, y: 20 }"
			:animate="{ opacity: 1, y: 0 }"
			:transition="{ duration: 0.3 }"
			class="mt-10 flex w-full max-w-4xl flex-col items-center text-center"
			role="status"
			aria-live="polite"
		>
			<div v-if="sessionError" class="flex flex-col gap-3">
				<Typography variant="title" class="text-on-surface"
					>Sign in unavailable</Typography
				>
				<Typography variant="body" class="text-on-surface-muted">
					{{ sessionError }}
				</Typography>
				<Button
					variant="primary"
					size="lg"
					:full-width="true"
					class="mx-auto mt-4 max-w-md text-white"
					@click="handleBack"
				>
					Back to login options
				</Button>
			</div>

			<div
				v-else-if="browserClosedWithoutAuth"
				class="flex flex-col gap-3"
			>
				<Typography variant="title" class="text-on-surface"
					>Sign in incomplete</Typography
				>
				<Typography variant="body" class="text-on-surface-muted">
					The browser closed but we couldn't complete sign in. Please
					try again.
				</Typography>
				<Button
					variant="primary"
					size="lg"
					:full-width="true"
					class="mx-auto mt-4 max-w-md text-white"
					@click="handleBack"
				>
					Back to login options
				</Button>
			</div>

			<template v-else>
				<Typography variant="headline" class="mt-6 text-on-surface">
					Redirecting to secure sign in
				</Typography>
				<Typography
					variant="body"
					class="mx-auto mt-3 max-w-md text-on-surface-muted"
				>
					You'll be redirected to the login page in a moment.
				</Typography>
				<Button
					class="mx-auto mt-4 max-w-md"
					variant="secondary"
					:full-width="true"
					:loading="true"
					@click="handleRedirect"
				>
					Click here if you're not redirected automatically
				</Button>
			</template>
		</motion.div>
	</Wizard>
</template>
